import { SearchInput } from '@/components/data-table/filters';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDeleteTravelEsimTariff } from '@/hooks/useTravelEsimTariffActions';
import { useTravelEsimTariffGrid } from '@/hooks/useTravelEsimTariffGrid';
import { ConfirmActionDialog } from '@/views/line-details/ConfirmActionDialog';
import type { TravelEsimTariff } from '@octave/contracts';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { TravelEsimTariffDialog } from './TravelEsimTariffDialog';
import { formatNumber, formatPrice } from '@/lib/format';
import {
  countTravelEsimDestinations,
  filterTravelEsimTariffs,
  formatTravelEsimData,
  formatTravelEsimDuration,
} from '@/lib/travel-esim';
import { useState } from 'react';

/**
 * The current international eSIM tariff grid, edited in place by Octave
 * admins: add an offer, change its price, data or duration, remove it.
 * Orders snapshot their prices, so a change only affects new quotes.
 */
export function TravelEsimTariffsView() {
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<{ tariff?: TravelEsimTariff } | null>(null);
  const [toDelete, setToDelete] = useState<TravelEsimTariff | null>(null);
  const deleteTariff = useDeleteTravelEsimTariff();
  const { data, isLoading, isError } = useTravelEsimTariffGrid();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Chargement de la grille tarifaire...</div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">
          Impossible de charger la grille tarifaire.
        </div>
      </div>
    );
  }

  const { tariffs } = data;
  const rows = filterTravelEsimTariffs(tariffs, search);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          placeholder="Rechercher une destination..."
          value={search}
          onChange={setSearch}
        />
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">
            {formatNumber(countTravelEsimDestinations(tariffs))} destinations ·{' '}
            {formatNumber(tariffs.length)} tarifs
          </span>
          <button
            type="button"
            onClick={() => setDialog({})}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-emerald-700 hover:shadow-md cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
            Ajouter un forfait
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl px-2 py-1">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Destination</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Durée</TableHead>
              <TableHead className="text-right">Prix HT par eSIM</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center text-slate-500 py-8"
                >
                  {tariffs.length === 0
                    ? 'Aucune grille tarifaire chargée.'
                    : 'Aucune destination ne correspond à la recherche.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((tariff) => (
                <TableRow key={tariff.id}>
                  <TableCell className="font-medium text-slate-900">
                    {tariff.country}
                  </TableCell>
                  <TableCell>
                    {formatTravelEsimData(tariff.dataLimitGb)}
                  </TableCell>
                  <TableCell>
                    {formatTravelEsimDuration(tariff.durationDays)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatPrice(tariff.priceCents)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setDialog({ tariff })}
                        aria-label="Modifier le forfait"
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setToDelete(tariff)}
                        aria-label="Supprimer le forfait"
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {dialog && (
        <TravelEsimTariffDialog tariff={dialog.tariff} onClose={() => setDialog(null)} />
      )}
      {toDelete && (
        <ConfirmActionDialog
          isOpen
          onClose={() => setToDelete(null)}
          onConfirm={() =>
            deleteTariff.mutate(toDelete.id, { onSuccess: () => setToDelete(null) })
          }
          title="Supprimer ce forfait ?"
          description={`${toDelete.country} · ${formatTravelEsimData(toDelete.dataLimitGb)} · ${formatTravelEsimDuration(toDelete.durationDays)} ne sera plus proposé.`}
          actionLabel="Supprimer"
          variant="destructive"
          isLoading={deleteTariff.isPending}
        />
      )}
    </div>
  );
}
